import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';

interface ConceptItem {
  key: string;
  value: string;
  editing: boolean;
  editValue: string;
}

@Component({
  selector: 'app-manage-learning-concepts-dialog',
  templateUrl: './manage-learning-concepts-dialog.component.html',
  styleUrls: ['./manage-learning-concepts-dialog.component.scss']
})
export class ManageLearningConceptsDialogComponent implements OnInit, OnDestroy {

  private readonly TYPE_PATH = 'types/Learning Concepts';

  concepts: ConceptItem[] = [];
  isLoading = true;
  isSaving = false;

  newConceptCtrl = new FormControl('', [Validators.required, Validators.minLength(2)]);

  private sub!: Subscription;

  constructor(
    public dialogRef: MatDialogRef<ManageLearningConceptsDialogComponent>,
    private mDatabase: AngularFireDatabase,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.sub = this.mDatabase.list(this.TYPE_PATH).snapshotChanges().subscribe(snaps => {
      this.concepts = snaps.map(s => ({
        key: s.key as string,
        value: s.payload.val() as string,
        editing: false,
        editValue: s.payload.val() as string
      }));
      this.isLoading = false;
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** Add a new concept */
  addConcept(): void {
    if (this.newConceptCtrl.invalid) {
      this.newConceptCtrl.markAsTouched();
      return;
    }
    const name = (this.newConceptCtrl.value as string).trim();
    if (!name) return;

    this.isSaving = true;
    this.mDatabase.list(this.TYPE_PATH).push(name)
      .then(() => {
        this.snackBar.open(`"${name}" added`, '', { duration: 2500, panelClass: ['success-snackbar'] });
        this.newConceptCtrl.reset();
      })
      .catch(err => {
        console.error(err);
        this.snackBar.open('Failed to add concept', '', { duration: 3000 });
      })
      .finally(() => this.isSaving = false);
  }

  /** Start inline edit */
  startEdit(item: ConceptItem): void {
    item.editValue = item.value;
    item.editing = true;
  }

  /** Cancel inline edit */
  cancelEdit(item: ConceptItem): void {
    item.editing = false;
  }

  /** Save inline edit */
  saveEdit(item: ConceptItem): void {
    const newVal = (item.editValue || '').trim();
    if (!newVal) return;

    this.isSaving = true;
    this.mDatabase.object(`${this.TYPE_PATH}/${item.key}`)
      .set(newVal)
      .then(() => {
        this.snackBar.open('Updated', '', { duration: 2000, panelClass: ['success-snackbar'] });
        item.editing = false;
      })
      .catch(err => {
        console.error(err);
        this.snackBar.open('Failed to update', '', { duration: 3000 });
      })
      .finally(() => this.isSaving = false);
  }

  /** Delete a concept */
  deleteConcept(item: ConceptItem): void {
    if (!confirm(`Delete "${item.value}"?`)) return;

    this.mDatabase.object(`${this.TYPE_PATH}/${item.key}`)
      .remove()
      .then(() => this.snackBar.open(`"${item.value}" deleted`, '', { duration: 2500 }))
      .catch(err => {
        console.error(err);
        this.snackBar.open('Failed to delete', '', { duration: 3000 });
      });
  }

  close(): void {
    this.dialogRef.close();
  }
}

